import {ErrorCode, ChannelViolation, SocialPlatform, AuthedPage, SocialChannel} from '../types';

export const mapViolationToCode = (violation, platform, config?) => {
    switch (violation) {
        case ChannelViolation.X_CONNECTED:
        case ChannelViolation.X_OAUTH_TOKEN:
            return ErrorCode.SOCIAL_CHANNEL_NO_OAUTH_TOKEN;
        case ChannelViolation.X_FOLLOWER_COUNT: {
            const {socialChannelsProperties} = config || {};
            const {MINIMUM_FOLLOWERS = 1000} = socialChannelsProperties || {};
            return {
                errorCode: ErrorCode.SOCIAL_CHANNEL_FOLLOWER_COUNT_NOT_MET,
                messageVars: [MINIMUM_FOLLOWERS]
            };
        }
        case ChannelViolation.X_OWNED:
            return ErrorCode.SOCIAL_CHANNEL_NOT_OWNED;
        case ChannelViolation.X_GOOD_STANDING:
            return ErrorCode.SOCIAL_CHANNEL_SUSPENDED;
        case ChannelViolation.X_PUBLIC:
            if (platform === SocialPlatform.INSTAGRAM) {
                return ErrorCode.SOCIAL_CHANNEL_PAGE_TYPE_INSTAGRAM;
            }
            return ErrorCode.SOCIAL_CHANNEL_PAGE_TYPE;
        default:
            return violation;
    }
};

export function transformViolation(error: string, platform: string, config?) {
    try {
        return mapViolationToCode(error, platform, config);
    } catch (err) {
        console.error(`Failed with: ${err.message}`);
        return null;
    }
}

export function sortViolation<T extends ChannelViolation | string>(violations: T[]): T[] {
    if (!violations || !violations.length) return violations;

    const priority = violations.map(error => {
        let priority = 4;
        if (error === ChannelViolation.X_OAUTH_TOKEN) {
            priority = 1;
        } else if (error === ChannelViolation.X_CONNECTED) {
            priority = 2;
        } else if (error === ChannelViolation.X_GOOD_STANDING) {
            priority = 3;
        }
        return {error, priority};
    });

    return priority.sort((a, b) => a.priority - b.priority).map(val => val.error);
}

export const mapViolation = (
    channel: SocialChannel | AuthedPage,
    hideDefaultErr?: boolean
): string[] => {
    const {id: isAlreadyAdded, oauthDetails, violations = []} = channel || {};
    const {valid: isAuthenticated} = oauthDetails || {};
    const msgCodes = [];

    if (!oauthDetails || oauthDetails.valid === false || violations.length) {
        if (!hideDefaultErr && !isAuthenticated) {
            msgCodes.push(ErrorCode.SOCIAL_CHANNEL_NOT_CONNECTED);
        }

        const standAloneError = [
            ChannelViolation.X_OAUTH_TOKEN,
            ChannelViolation.X_CONNECTED,
            ChannelViolation.X_GOOD_STANDING
        ];

        for (const error of sortViolation(violations) as ChannelViolation[]) {
            if (typeof error === 'string') {
                if (standAloneError.includes(error)) {
                    msgCodes.push(error);
                    break;
                } else {
                    msgCodes.push(error);
                }
            }
        }
    } else if (isAlreadyAdded) {
        msgCodes.push(ErrorCode.SOCIAL_CHANNEL_ALREADY_ADDED);
    } else {
        msgCodes.push(ErrorCode.SOCIAL_CHANNEL_SOMETHING_WRONG);
    }

    return msgCodes;
};

export const mapPlatformToUrl = (platform: SocialPlatform, channelId: string): string => {
    switch (platform) {
        case SocialPlatform.YOUTUBE_CHANNEL: {
            return `https://www.youtube.com/channel/${channelId}`;
        }
        case SocialPlatform.TWITCH_CHANNEL: {
            return `https://www.twitch.tv/${channelId}`;
        }
        case SocialPlatform.FACEBOOK: {
            return `https://www.facebook.com/${channelId}`;
        }
        case SocialPlatform.VK: {
            return `https://www.vk.com/${channelId}`;
        }
        case SocialPlatform.TWITTER: {
            return `https://www.twitter.com/${channelId}`;
        }
        case SocialPlatform.INSTAGRAM: {
            return `https://www.instagram.com/${channelId}`;
        }
        default: {
            console.error(`Unsupported platform: ${platform}, channelId: ${channelId}`);
            throw new Error(ErrorCode.SOCIAL_CHANNEL_UNSUPPORTED);
        }
    }
};
